import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { ORDER_STATUS_DISPLAY, OrderPastDueSummary, PastDueSeverity, UserRole } from '../../types';
import { useGetActiveDealersByManufacturerIdQuery } from '../dealer/dealer-api-slice';
import { useGetOrdersPastDueReportQuery } from '../order/orders-api-slice';
import { selectCurrentUser } from '../user';
import {
  ManufacturerFilter,
  ReportLayout,
  ReportTablePagination,
  SortableHeadCell,
  useSortableTable,
} from './components';
import { formatReportCurrency, formatReportDate } from './report-utils';

const SEVERITY_RANK: Record<PastDueSeverity, number> = {
  [PastDueSeverity.CRITICAL]: 3,
  [PastDueSeverity.OVERDUE]: 2,
  [PastDueSeverity.LATE]: 1,
};

const ORDERS_PAST_DUE_SORT_ACCESSORS: Record<
  string,
  (order: OrderPastDueSummary) => string | number
> = {
  severity: o => SEVERITY_RANK[o.severity] ?? 0,
  orderNumber: o => o.orderNumber,
  dealerName: o => o.dealerName ?? '',
  customer: o => o.customer,
  dueDate: o => o.dueDate,
  daysLate: o => o.daysLate,
  status: o => ORDER_STATUS_DISPLAY[o.status] ?? `Status ${o.status}`,
  total: o => o.total,
};

const SEVERITY_CONFIG: Record<
  PastDueSeverity,
  {
    label: string;
    badgeBg: string;
    badgeColor: string;
    rowBg: string;
    borderColor: string;
    daysColor: string;
  }
> = {
  [PastDueSeverity.CRITICAL]: {
    label: 'CRITICAL',
    badgeBg: '#e74c3c',
    badgeColor: '#ffffff',
    rowBg: '#ffe8e8',
    borderColor: '#e74c3c',
    daysColor: '#e74c3c',
  },
  [PastDueSeverity.OVERDUE]: {
    label: 'OVERDUE',
    badgeBg: '#ff9800',
    badgeColor: '#663d00',
    rowBg: '#fff5f3',
    borderColor: '#ff9800',
    daysColor: '#ff9800',
  },
  [PastDueSeverity.LATE]: {
    label: 'LATE',
    badgeBg: '#ffc107',
    badgeColor: '#654d00',
    rowBg: '#fffbf0',
    borderColor: '#ffc107',
    daysColor: '#f39c12',
  },
};

function formatDaysLate(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

function getSeverityConfig(order: OrderPastDueSummary) {
  return SEVERITY_CONFIG[order.severity] ?? SEVERITY_CONFIG[PastDueSeverity.LATE];
}

const TABLE_HEADER_SX = { fontWeight: 'bold', bgcolor: '#34495e', color: 'white' } as const;

export default function OrdersPastDueReport() {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);

  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const isManufacturer =
    isAdmin || currentUser?.role === UserRole.SUPERVISOR || currentUser?.role === UserRole.CSR;

  const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(undefined);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    undefined
  );

  const resolvedManufacturerId =
    isAdmin && selectedManufacturerId ? selectedManufacturerId : currentUser?.manufacturerId;

  const { data: dealers = [] } = useGetActiveDealersByManufacturerIdQuery(resolvedManufacturerId!, {
    skip: !isManufacturer || !resolvedManufacturerId,
  });

  const {
    data: reportData,
    isLoading,
    error,
  } = useGetOrdersPastDueReportQuery(
    {
      dealerId: selectedDealerId,
      manufacturerId: resolvedManufacturerId,
    },
    { skip: !resolvedManufacturerId }
  );

  const handleManufacturerChange = useCallback((mfgId: number | undefined) => {
    setSelectedManufacturerId(mfgId);
    setSelectedDealerId(undefined);
  }, []);

  const needsManufacturer = isAdmin && !resolvedManufacturerId;

  const { pageRows, page, rowsPerPage, getSortProps, handleChangePage, handleChangeRowsPerPage } =
    useSortableTable<OrderPastDueSummary>({
      data: reportData?.orders ?? [],
      accessors: ORDERS_PAST_DUE_SORT_ACCESSORS,
      initialSortField: 'daysLate',
      initialSortDirection: 'desc',
    });

  return (
    <ReportLayout
      title="Orders Past Due"
      description="Identify overdue orders requiring immediate attention, with severity indicators to help prioritize follow-up actions."
      isLoading={false}
      error={null}
      filters={
        <>
          <ManufacturerFilter
            isAdmin={isAdmin}
            selectedManufacturerId={selectedManufacturerId}
            onManufacturerChange={handleManufacturerChange}
          />
          {isManufacturer && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="dealer-filter-label" shrink>
                Dealer
              </InputLabel>
              <Select
                labelId="dealer-filter-label"
                value={selectedDealerId ?? ''}
                label="Dealer"
                displayEmpty
                notched
                onChange={e => {
                  const val = e.target.value;
                  setSelectedDealerId(val === '' ? undefined : Number(val));
                }}
                disabled={!resolvedManufacturerId}
                renderValue={selected => {
                  if (!selected) return 'All Dealers';
                  const dealer = dealers.find(d => d.id === Number(selected));
                  return dealer?.name ?? '';
                }}
              >
                <MenuItem value="">All Dealers</MenuItem>
                {dealers.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      }
    >
      {needsManufacturer ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">
            Please select a Manufacturer to view the report.
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading report...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">Unable to retrieve past due orders. Please try again.</Alert>
      ) : reportData ? (
        <>
          {reportData.totalCount > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{
                mb: 3,
                '& .MuiAlert-message': { width: '100%', textAlign: 'center' },
                border: '2px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                <Box
                  component="span"
                  sx={{ color: 'error.main', fontSize: '1.5rem', fontWeight: 'bold' }}
                >
                  {reportData.totalCount}
                </Box>{' '}
                orders are currently past due and require immediate attention
              </Typography>
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" align="center" gutterBottom>
                All Past Due Orders
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Sorted by days overdue (most overdue first) — click any column header to re-sort
              </Typography>

              {reportData.orders.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No past due orders found.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('severity')}>
                          Severity
                        </SortableHeadCell>
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('orderNumber')}>
                          Order #
                        </SortableHeadCell>
                        {isManufacturer && (
                          <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('dealerName')}>
                            Dealer
                          </SortableHeadCell>
                        )}
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('customer')}>
                          Customer
                        </SortableHeadCell>
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('dueDate')}>
                          Due Date
                        </SortableHeadCell>
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('daysLate')}>
                          Days Late
                        </SortableHeadCell>
                        <SortableHeadCell sx={TABLE_HEADER_SX} {...getSortProps('status')}>
                          Status
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={TABLE_HEADER_SX}
                          align="right"
                          {...getSortProps('total')}
                        >
                          Total
                        </SortableHeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map(order => {
                        const config = getSeverityConfig(order);
                        return (
                          <TableRow
                            key={order.id}
                            sx={{
                              backgroundColor: config.rowBg,
                              borderLeft: `4px solid ${config.borderColor}`,
                              '&:hover': { opacity: 0.9 },
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={config.label}
                                size="small"
                                sx={{
                                  bgcolor: config.badgeBg,
                                  color: config.badgeColor,
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Link
                                component="button"
                                variant="body2"
                                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                onClick={() => navigate(`/order-details/${order.id}`)}
                              >
                                {order.orderNumber}
                              </Link>
                            </TableCell>
                            {isManufacturer && <TableCell>{order.dealerName ?? '—'}</TableCell>}
                            <TableCell>{order.customer}</TableCell>
                            <TableCell>{formatReportDate(order.dueDate)}</TableCell>
                            <TableCell>
                              <Typography
                                component="span"
                                sx={{ fontWeight: 'bold', color: config.daysColor }}
                              >
                                {formatDaysLate(order.daysLate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {ORDER_STATUS_DISPLAY[order.status] ?? `Status ${order.status}`}
                            </TableCell>
                            <TableCell align="right">{formatReportCurrency(order.total)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <ReportTablePagination
                    count={reportData.orders.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </ReportLayout>
  );
}
